import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  memo,
} from "react";
import { FaTimes, FaSearch, FaUser } from "react-icons/fa";

/**
 * Step 1: pick target employee for החלפת חתימות (admin/parentsUser).
 */
function SignatureTransferModal({
  isOpen,
  onClose,
  personnel,
  sourceEmployee,
  assignedCount,
  onContinueToSignature,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selectedTarget, setSelectedTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setDebounced("");
      setSelectedTarget(null);
    }
  }, [isOpen]);

  const sourcePn = sourceEmployee?.personalNumber;

  const candidates = useMemo(() => {
    if (!personnel?.length) return [];
    return personnel.filter(
      (p) => p.personalNumber && p.personalNumber !== sourcePn,
    );
  }, [personnel, sourcePn]);

  const filtered = useMemo(() => {
    if (!debounced) return candidates.slice(0, 80);
    const term = debounced.toLowerCase();
    return candidates
      .filter((p) => {
        const fn = (p.firstName || "").toLowerCase();
        const ln = (p.lastName || "").toLowerCase();
        const pn = String(p.personalNumber || "");
        return (
          fn.includes(term) ||
          ln.includes(term) ||
          pn.includes(term) ||
          `${fn} ${ln}`.includes(term)
        );
      })
      .slice(0, 80);
  }, [candidates, debounced]);

  const handleContinue = useCallback(() => {
    if (!selectedTarget) {
      alert("נא לבחור חייל יעד");
      return;
    }
    onContinueToSignature(selectedTarget);
  }, [selectedTarget, onContinueToSignature]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay signature-transfer-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content signature-transfer-modal"
        role="dialog"
        aria-labelledby="signature-transfer-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="signature-transfer-title">החלפת חתימות</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="סגור"
          >
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          <p className="signature-transfer-intro">
            כל הפריטים המוחתמים ל־
            <strong>
              {" "}
              {sourceEmployee?.firstName} {sourceEmployee?.lastName}
            </strong>{" "}
            (מ.א {sourcePn}) יועברו לחייל שתבחרו, לאחר חתימה.
          </p>
          <p className="signature-transfer-count">
            סה״כ פריטים להעברה: <strong>{assignedCount}</strong>
          </p>

          <div className="signature-transfer-search">
            <FaSearch className="signature-transfer-search-icon" aria-hidden />
            <input
              type="text"
              className="signature-transfer-search-input"
              placeholder="חפש חייל לפי שם או מספר אישי..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
          </div>

          {selectedTarget && (
            <div className="signature-transfer-selected">
              <FaUser aria-hidden />
              <span>
                נבחר: {selectedTarget.firstName} {selectedTarget.lastName} (
                {selectedTarget.personalNumber})
              </span>
            </div>
          )}

          <div className="signature-transfer-list" role="listbox">
            {filtered.length === 0 ? (
              <div className="signature-transfer-empty">לא נמצאו תוצאות</div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id || p.personalNumber}
                  type="button"
                  role="option"
                  aria-selected={
                    selectedTarget?.personalNumber === p.personalNumber
                  }
                  className={
                    selectedTarget?.personalNumber === p.personalNumber
                      ? "signature-transfer-row is-selected"
                      : "signature-transfer-row"
                  }
                  onClick={() => setSelectedTarget(p)}
                >
                  <span className="signature-transfer-row-name">
                    {p.firstName} {p.lastName}
                  </span>
                  <span className="signature-transfer-row-pn">
                    מ.א {p.personalNumber}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
        <div className="modal-footer signature-transfer-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            ביטול
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleContinue}
            disabled={!selectedTarget}
          >
            המשך לחתימה
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(SignatureTransferModal);
